import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LogOut,
  MapPin,
  Menu,
  Package,
  ShieldCheck,
  UserRound,
  X,
  Heart,
  Star,
  Ticket,
  CreditCard,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { label: 'My Profile', href: '/profile', tab: 'profile', icon: UserRound },
  { label: 'Manage Addresses', href: '/profile?tab=addresses', tab: 'addresses', icon: Home },
  { label: 'My Orders', href: '/orders', tab: 'orders', icon: Package },
  { label: 'My Wishlist', href: '/profile?tab=wishlist', tab: 'wishlist', icon: Heart },
  { label: 'My Reviews & Ratings', href: '/profile?tab=reviews', tab: 'reviews', icon: Star },
  { label: 'Coupons & Offers', href: '/profile?tab=coupons', tab: 'coupons', icon: Ticket },
  { label: 'PAN & GST Details', href: '/profile?tab=pan', tab: 'pan', icon: CreditCard },
];

const UserPanelShell = ({ title, subtitle, children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get('tab') || 'profile';

  const isActive = (item) => {
    if (item.href === '/orders') {
      return location.pathname.startsWith('/orders');
    }
    if (location.pathname === '/profile') {
      return (currentTab === item.tab) || (!searchParams.get('tab') && item.tab === 'profile');
    }
    return false;
  };

  const SidebarContent = () => (
    <>
      {/* Flipkart Style User Card Header */}
      <div className="flex items-center gap-3 border-b border-[#3e2f20] pb-5">
        <div className="h-14 w-14 rounded-full bg-[#a97752] grid place-items-center text-xl font-bold uppercase text-[#f4e9db] shadow-inner flex-shrink-0">
          {(user?.name || 'Customer').slice(0, 1)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[#b39b81]">Hello,</p>
          <p className="font-bold text-[#f4e9db] truncate">{user?.name || 'Valued Customer'}</p>
          <p className="text-xs text-[#b39b81] truncate">{user?.email}</p>
        </div>
        <button onClick={() => setMenuOpen(false)} className="lg:hidden p-1.5 text-[#d8c3aa] hover:text-white" aria-label="Close menu">
          <X size={20} />
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="mt-4 space-y-1">
        {navItems.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center justify-between px-3.5 py-3 rounded-xl text-xs sm:text-sm font-medium transition ${
                active ? 'bg-[#3d291d] text-[#f6e8d6] font-semibold shadow-sm' : 'text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <item.icon size={18} className={active ? 'text-[#E8B530]' : 'text-[#d8c3aa]'} />
                <span className="truncate">{item.label}</span>
              </div>
              <ChevronRight size={14} className={`opacity-60 ${active ? 'text-[#E8B530]' : ''}`} />
            </Link>
          );
        })}

        <div className="pt-3 border-t border-[#3e2f20]/60 space-y-1">
          <Link
            to="/stores"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs sm:text-sm font-medium text-[#d8c3aa] hover:bg-[#221a14] hover:text-[#f8efe5]"
          >
            <MapPin size={18} className="text-[#E8B530]" />
            <span>FOSEN Store Locator</span>
          </Link>

          {isAdmin && (
            <Link
              to="/admin/dashboard"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs sm:text-sm font-medium text-[#E8B530] bg-[#E8B530]/10 hover:bg-[#E8B530]/20"
            >
              <ShieldCheck size={18} />
              <span>Admin Panel</span>
            </Link>
          )}

          <button
            onClick={logout}
            className="flex w-full items-center gap-3 px-3.5 py-3 rounded-xl text-xs sm:text-sm font-medium text-red-300 hover:bg-red-950/40 hover:text-red-200 transition"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </nav>
    </>
  );

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Mobile Header Menu Trigger */}
        <div className="lg:hidden mb-4 flex items-center justify-between bg-[#1F2A44] text-[#FAF7F2] p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#E8B530] text-[#1F2A44] font-bold grid place-items-center text-sm uppercase">
              {(user?.name || 'C').slice(0, 1)}
            </div>
            <div>
              <p className="text-xs text-[#FAF7F2]/70">Flipkart Style Account</p>
              <p className="font-semibold text-sm truncate">{title}</p>
            </div>
          </div>
          <button
            onClick={() => setMenuOpen(true)}
            className="inline-flex items-center gap-2 bg-[#E8B530] text-[#1F2A44] px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider"
            aria-label="Open account menu"
          >
            <Menu size={16} />
            Menu
          </button>
        </div>

        {/* Mobile Backdrop */}
        {menuOpen && (
          <button
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label="Close account menu overlay"
          />
        )}

        {/* Mobile Sidebar Drawer */}
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-[295px] bg-[#0c0907] text-[#f4e9db] border-r border-[#3e2f20] p-5 shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarContent />
        </aside>

        {/* Layout Grid */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Desktop Sticky Sidebar */}
          <aside className="hidden lg:block bg-[#0c0907] text-[#f4e9db] border border-[#3e2f20] rounded-2xl p-5 h-fit lg:sticky lg:top-28 shadow-lg">
            <SidebarContent />
          </aside>

          {/* Main Dashboard Panel View */}
          <section className="min-w-0">
            <div className="hidden lg:block mb-6 bg-white border border-[#1F2A44]/10 rounded-2xl p-6 shadow-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-[#8b5e34] font-semibold">Customer Dashboard</p>
              <h1 className="font-serif text-3xl font-bold text-[#1F2A44] mt-1">{title}</h1>
              {subtitle && <p className="text-sm text-[#1F2A44]/70 mt-1.5">{subtitle}</p>}
            </div>
            {children}
          </section>
        </div>
      </div>
    </main>
  );
};

export default UserPanelShell;
